import api from './api';

const jobPhotoService = {
  // ============================================
  // GET METHODS
  // ============================================

  // Get all photos for a job
  getJobPhotos: async (jobId) => {
    return await api.get(`/jobphotos/job/${jobId}`);
  },

  // Get photos grouped by type with limit info
  getJobPhotosGrouped: async (jobId) => {
    return await api.get(`/jobphotos/job/${jobId}/grouped`);
  },

  // Get photo summary for job card preview (count, preview URL)
  getJobPhotoSummary: async (jobId) => {
    return await api.get(`/jobphotos/job/${jobId}/summary`);
  },

  // Get all photos for a customer (across all their jobs)
  getCustomerPhotos: async (customerId) => {
    return await api.get(`/jobphotos/customer/${customerId}`);
  },

  // Get photo limits for current business tier
  getLimits: async () => {
    return await api.get('/jobphotos/limits');
  },

  // Check if more photos can be added to a job
  canAddPhotos: async (jobId) => {
    return await api.get(`/jobphotos/job/${jobId}/can-add`);
  },

  // ============================================
  // UPLOAD METHODS
  // ============================================

  // Upload a photo (multipart form - recommended for simplicity)
  uploadPhoto: async (jobId, type, file, caption = null, latitude = null, longitude = null, pairedWithPhotoId = null, takenAt = null) => {
    const formData = new FormData();
    formData.append('jobId', jobId);
    formData.append('type', type);
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    if (latitude) formData.append('latitude', latitude);
    if (longitude) formData.append('longitude', longitude);
    if (pairedWithPhotoId) formData.append('pairedWithPhotoId', pairedWithPhotoId);
    if (takenAt) formData.append('takenAt', takenAt);

    return await api.post('/jobphotos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Increase timeout for large files
      timeout: 60000,
    });
  },

  // ============================================
  // UPDATE METHODS
  // ============================================

  // Update photo details (caption, type, sort order)
  updatePhoto: async (photoId, data) => {
    return await api.put(`/jobphotos/${photoId}`, data);
  },

  // Update just the caption
  updateCaption: async (photoId, caption) => {
    return await api.put(`/jobphotos/${photoId}`, { caption });
  },

  // Update just the type
  updateType: async (photoId, type) => {
    return await api.put(`/jobphotos/${photoId}`, { type });
  },

  // Reorder photos for a job
  reorderPhotos: async (jobId, photoIds) => {
    return await api.post(`/jobphotos/job/${jobId}/reorder`, { photoIds });
  },

  // ============================================
  // DELETE METHODS
  // ============================================

  // Delete a single photo
  deletePhoto: async (photoId) => {
    return await api.delete(`/jobphotos/${photoId}`);
  },

  // Delete all photos for a job
  deleteAllJobPhotos: async (jobId) => {
    return await api.delete(`/jobphotos/job/${jobId}/all`);
  },

  // ============================================
  // DOWNLOAD METHODS
  // ============================================

  // Download all photos for a job as ZIP
  downloadPhotosZip: async (jobId) => {
    return await api.get(`/jobphotos/job/${jobId}/download`, {
      responseType: 'blob',
    });
  },

  // ============================================
  // SHARE METHODS
  // ============================================

  // Generate a shareable link for job photos
  generateShareLink: async (jobId, expiresInDays = 7) => {
    return await api.post(`/jobphotos/job/${jobId}/share`, { expiresInDays });
  },

  // Get shared photos (public endpoint - no auth required)
  getSharedPhotos: async (shareToken) => {
    return await api.get(`/jobphotos/shared/${shareToken}`);
  },

  // Revoke a share link
  revokeShareLink: async (jobId) => {
    return await api.delete(`/jobphotos/job/${jobId}/share`);
  },

  // ============================================
  // WEBSITE GALLERY APPROVAL METHODS
  // ============================================
 
  // Toggle website approval for a photo (and its paired photo)
  toggleWebsiteApproval: async (photoId, isApproved) => {
    return await api.patch(`/jobphotos/${photoId}/website-approve`, { isApproved });
  },
 
  // Toggle featured status for a photo (homepage rotation)
  toggleFeatured: async (photoId, isFeatured) => {
    return await api.patch(`/jobphotos/${photoId}/featured`, { isFeatured });
  },
 
  // Get all website-approved photos for this business
  getWebsiteApprovedPhotos: async () => {
    return await api.get('/jobphotos/website-approved');
  },
  
};

export default jobPhotoService;